import { z } from "zod";
import { addressSchema, TimeZoneSchema } from "./utils";

/**
 * Single-hospital setup: the Hospital table holds exactly one row, treated as
 * facility settings (display name + address + logo) and managed via
 * GET/PUT /api/dashboard/hospital.
 */

/**
 * The facility's logo, small enough to travel INSIDE the payloads that already
 * carry the facility name — the dashboard's settings read and the patient's own
 * profile. A `data:` URL rather than a URL to fetch: both clients already hold
 * a warm copy of those payloads, so naming the facility with its logo costs no
 * request, and an <img>/RN Image source needs no auth header, no CORS rule and
 * no X-Api-Key on an image request it cannot add one to.
 *
 * That is only sound while it stays small, hence the cap. The dashboard
 * downscales whatever an admin picks before it ever reaches the API.
 */
export const HOSPITAL_LOGO_MAX_BYTES = 256 * 1024;
/** base64 inflates 4 chars per 3 bytes; the prefix is ~30 more. */
const LOGO_DATA_URL_MAX_CHARS = Math.ceil(HOSPITAL_LOGO_MAX_BYTES / 3) * 4 + 64;

export const hospitalLogoSchema = z.object({
  /**
   * `data:image/(png|jpeg|webp);base64,...`. The mime list is a whitelist, not
   * a formality: SVG would be markup rendered from the database, and React
   * Native cannot display it anyway.
   */
  dataUrl: z
    .string()
    .max(LOGO_DATA_URL_MAX_CHARS)
    .regex(
      /^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/]+={0,2}$/,
      "Expected a base64 data URL for a PNG, JPEG or WebP image",
    ),
  /**
   * width / height of the image, recorded by whoever uploaded it.
   *
   * It travels with the bytes because the clients that lay the logo out cannot
   * cheaply measure them: React Native has no synchronous intrinsic size, so
   * without this a logo either letterboxes inside a fixed box or makes the
   * layout jump once it decodes. One number, written in the same statement as
   * the image, so the two can never disagree.
   */
  aspectRatio: z.number().positive().max(20),
});
export type HospitalLogo = z.infer<typeof hospitalLogoSchema>;

export const hospitalSettingsSchema = z.object({
  name: z.string(),
  address: addressSchema.nullable(),
  /** Null until an admin uploads one — every client falls back to text. */
  logo: hospitalLogoSchema.nullable(),
  /**
   * The facility's IANA timezone. Every survey schedule's time of day is
   * resolved in it, deliberately not in the patient's device zone: a study's
   * "morning questionnaire" has to mean the same instant for everyone in the
   * cohort, and a patient travelling must not shift their own series.
   *
   * READ-ONLY through the API: it is seeded once per deployment
   * (PC_DEFAULT_HOSPITAL_TIMEZONE) and the PUT below does not accept it.
   * Occurrences are derived on every read, so this value is an input to every
   * historical answering window; an edit would retroactively rewrite which
   * questionnaires were open when. It appears here so clients can display it.
   */
  timezone: TimeZoneSchema,
});
export type HospitalSettings = z.infer<typeof hospitalSettingsSchema>;

export const getHospitalSettingsSchemas = {
  response: { 200: hospitalSettingsSchema },
};

export const updateHospitalSettingsSchemas = {
  // No `timezone`, deliberately (2026-08-12): the column is write-once at seed
  // time. Every derived occurrence resolves through it, so accepting it here
  // would let a settings edit move every historical answering window and flip
  // submissions near a window edge into answers to windows that were never
  // open. A body that sends one anyway is silently stripped, like any unknown
  // key.
  body: z.object({
    name: z.string().min(1),
    address: addressSchema.optional(),
    /**
     * Three states, deliberately: omitted leaves the stored logo alone (the
     * settings form saves a name and an address without re-uploading an image
     * every time), `null` removes it, an object replaces it.
     */
    logo: hospitalLogoSchema.nullable().optional(),
  }),
  response: { 200: hospitalSettingsSchema },
};
export type UpdateHospitalSettingsBody = z.infer<
  typeof updateHospitalSettingsSchemas.body
>;
